import { useEffect, useMemo, useState } from 'react';

import { log } from '@methanesat/log';

import { LoadedData } from '../../types';
import { customFetch, useErrorBoundary } from '../../utils';
import { noop } from 'lodash';

export type FetchOptions = {
    method?: string;
    body?: Blob | ArrayBuffer | DataView | FormData | URLSearchParams | string;
    mode?: RequestMode;
    credentials?: RequestCredentials;
    cache?: RequestCache;
    headers?: HeadersInit;
    redirect?: RequestRedirect;
    referrer?: string;
    referrerPolicy?: ReferrerPolicy;
    integrity?: string;
    keepAlive?: string;
    signal?: AbortSignal;
};

/**
 * Returns data for a given url.
 */
export const useDataAPI = function <Data = LoadedData | LoadedData[]>(
    dataUrl: string | null,
    { disableErrorBoundary, ...options }: FetchOptions & { disableErrorBoundary?: boolean } = {},
    errorHandler: (e: unknown) => void = noop
): Data | undefined {
    /**
     * assign a generic, empty object to the state initially. should be okay
     * for both geojson and arrays of datapoints.
     */
    const [loadedData, setLoadedData] = useState<Data | undefined>();

    // Used to activate the error boundary surrounding the component
    const { showErrorBoundary } = useErrorBoundary();
    const modifiedErrorHandler = disableErrorBoundary ? errorHandler : showErrorBoundary;

    const stableFetchOptions = useMemo(
        () => options,
        // Avoid using `options` directly. If it's empty, a new reference is created on every render,
        // which would retrigger the effect below unnecessarily.
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [
            options.method,
            options.body,
            options.mode,
            options.credentials,
            options.cache,
            options.headers,
            options.redirect,
            options.referrer,
            options.referrerPolicy,
            options.integrity,
            options.keepAlive
        ]
    );

    /**
     * update loadedData if/when the url changes
     * and add/create new controller
     */
    useEffect(() => {
        const controller = new AbortController();
        async function getData(dataUrl: string | null) {
            if (!dataUrl) return;

            // load data & set
            try {
                const res = await customFetch(dataUrl, {
                    ...stableFetchOptions,
                    // pass controller.signal to options to allow aborting the request
                    signal: controller.signal
                });

                if (!res.ok) {
                    throw new Error(`Network request at ${res.url} failed with status code ${res.status}`);
                }

                const jsonData = await res.json();

                setLoadedData(jsonData);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    log.warn(`useDataAPI aborted request at url ${dataUrl}\n${e}`);
                } else {
                    // The only time we want to disable the error boundary is if
                    // we are fetching data for the deckgl layers. This is because
                    // deckgl doesn't provide a nice interface for ErorrBoundaries and
                    // their own layers.
                    if (disableErrorBoundary) {
                        log.error(`useDataAPI failed for url ${dataUrl}\n${e}`);
                    }
                    modifiedErrorHandler(e);
                }
            }
        }

        getData(dataUrl);

        // clean up the last request
        return () => {
            controller.abort();
        };
    }, [dataUrl, disableErrorBoundary, modifiedErrorHandler, stableFetchOptions]);

    return loadedData;
};
